<template>
    <div class="card card-reports">
        <header>
            <i class="fa-solid fa-envelope"></i>
            {{ $t('dash.settings.notifications.reports.title') }}
            <BetaBadge></BetaBadge>
        </header>

        <div class="content">
            <div class="reports-head">
                <p class="reports-intro">{{ $t('dash.settings.notifications.reports.subtitle') }}</p>
                <div class="btn-primary icon-l" :class="{ disabled: schedules.length >= maxSchedules }" @click="openCreatePopup">
                    <i class="fa-solid fa-plus"></i><span>{{ $t('dash.settings.notifications.reports.add') }}</span>
                </div>
            </div>

            <div class="reports-tabs">
                <span :class="{ active: tab === 'schedules' }" @click="tab = 'schedules'">{{ $t('dash.settings.notifications.reports.tabs.schedules') }}</span>
                <span :class="{ active: tab === 'history' }" @click="openHistory">{{ $t('dash.settings.notifications.reports.tabs.history') }}</span>
            </div>

            <!-- Bilans programmés -->
            <template v-if="tab === 'schedules'">
                <div class="reports-loading" v-if="loading"><i class="fa-solid fa-spinner fa-spin"></i></div>

                <div class="reports-empty" v-else-if="!schedules.length">
                    <i class="fa-regular fa-calendar-plus"></i>
                    <p class="empty-title">{{ $t('dash.settings.notifications.reports.empty.title') }}</p>
                    <p>{{ $t('dash.settings.notifications.reports.empty.text') }}</p>
                </div>

                <div class="reports-list" v-else>
                    <div class="report-row" v-for="schedule in schedules" :key="schedule.id" :class="{ paused: !schedule.enabled }">
                        <div class="report-main">
                            <p class="report-name">{{ schedule.name }}</p>
                            <p class="report-meta">
                                <span><i class="fa-regular fa-repeat"></i> {{ frequencyLabel(schedule) }}</span>
                                <span v-if="schedule.enabled"><i class="fa-regular fa-paper-plane"></i> {{ $t('dash.settings.notifications.reports.next_send', { date: formatDate(schedule.next_send_at) }) }}</span>
                                <span v-else class="paused-label">{{ $t('dash.settings.notifications.reports.paused') }}</span>
                                <Tooltip v-if="schedule.cc_emails && schedule.cc_emails.length" :text="schedule.cc_emails.join(', ')" :position="'top'">
                                    <span><i class="fa-regular fa-user-group"></i> {{ $t('dash.settings.notifications.reports.cc_count', { n: schedule.cc_emails.length }) }}</span>
                                </Tooltip>
                            </p>
                        </div>

                        <div class="report-pages">
                            <span class="all-pages" v-if="!schedule.page_ids || !schedule.page_ids.length">{{ $t('dash.settings.notifications.reports.all_pages') }}</span>
                            <template v-else>
                                <Tooltip v-for="page in schedulePages(schedule).slice(0, 5)" :key="page.id" :text="page.name" :position="'top'">
                                    <Avatar :urlPicture="page.avatar" :typeNetwork="page.network"></Avatar>
                                </Tooltip>
                                <span class="more" v-if="schedulePages(schedule).length > 5">+{{ schedulePages(schedule).length - 5 }}</span>
                            </template>
                        </div>

                        <div class="report-actions">
                            <ComponentSwitch :value="!!schedule.enabled" :onChange="toggleEnabled" :id="schedule.id"></ComponentSwitch>
                            <Tooltip :text="$t('dash.settings.notifications.reports.actions.send_now')" :position="'top'">
                                <div class="icon-btn" :class="{ disabled: sending[schedule.id] }" @click="sendNow(schedule)">
                                    <i :class="sending[schedule.id] ? 'fa-solid fa-spinner fa-spin' : 'fa-regular fa-paper-plane'"></i>
                                </div>
                            </Tooltip>
                            <Tooltip :text="$t('dash.settings.notifications.reports.actions.edit')" :position="'top'">
                                <div class="icon-btn" @click="openEditPopup(schedule)"><i class="fa-regular fa-pen"></i></div>
                            </Tooltip>
                            <Tooltip :text="$t('dash.settings.notifications.reports.actions.delete')" :position="'top'">
                                <div class="icon-btn danger" @click="remove(schedule)"><i class="fa-regular fa-trash-can"></i></div>
                            </Tooltip>
                        </div>
                    </div>
                </div>
            </template>

            <!-- Historique des bilans reçus -->
            <template v-else>
                <div class="reports-loading" v-if="historyLoading"><i class="fa-solid fa-spinner fa-spin"></i></div>

                <div class="reports-empty" v-else-if="!deliveries.length">
                    <i class="fa-regular fa-inbox"></i>
                    <p>{{ $t('dash.settings.notifications.reports.history.empty') }}</p>
                </div>

                <div class="reports-list" v-else>
                    <div class="delivery-row" v-for="delivery in deliveries" :key="delivery.id">
                        <div class="report-main">
                            <p class="report-name">{{ delivery.report_name }}</p>
                            <p class="report-meta">
                                <span><i class="fa-regular fa-calendar"></i> {{ delivery.period_label }}</span>
                                <span><i class="fa-regular fa-paper-plane"></i> {{ $t('dash.settings.notifications.reports.history.sent_at', { date: formatDateTime(delivery.sent_at) }) }}</span>
                            </p>
                        </div>
                        <div class="delivery-pdfs">
                            <span class="no-pdf" v-if="!delivery.pdfs.length">{{ $t('dash.settings.notifications.reports.history.no_pdf') }}</span>
                            <template v-else>
                                <template v-if="!expandedDeliveries[delivery.id]">
                                    <Tooltip v-for="pdf in delivery.pdfs.slice(0, maxInlinePdfs)" :key="pdf.id" :text="$t('dash.settings.notifications.reports.history.download')" :position="'top'">
                                        <div class="pdf-chip" :class="{ disabled: downloading[pdf.id] }" @click="downloadPdf(delivery, pdf)">
                                            <Avatar :urlPicture="pageAvatar(pdf)" :typeNetwork="pdf.network || 'facebook'"></Avatar>
                                            <div class="pdf-page">
                                                <span class="pdf-name">{{ pdf.page_name || pdf.filename }}</span>
                                                <span class="pdf-network">{{ networkLabel(pdf.network) }}</span>
                                            </div>
                                            <i :class="downloading[pdf.id] ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'"></i>
                                        </div>
                                    </Tooltip>
                                </template>
                                <div class="pdf-more" v-if="delivery.pdfs.length > maxInlinePdfs" @click="toggleDelivery(delivery.id)">
                                    <template v-if="expandedDeliveries[delivery.id]">
                                        <i class="fa-solid fa-chevron-up"></i> {{ $t('dash.settings.notifications.reports.history.show_less') }}
                                    </template>
                                    <template v-else>
                                        +{{ delivery.pdfs.length - maxInlinePdfs }} · {{ $t('dash.settings.notifications.reports.history.show_all', { n: delivery.pdfs.length }) }}
                                    </template>
                                </div>
                            </template>
                        </div>

                        <!-- Tous les PDF du bilan, en grille sous la ligne -->
                        <div class="pdf-panel" v-if="expandedDeliveries[delivery.id]">
                            <input
                                v-if="delivery.pdfs.length > 12"
                                type="text"
                                class="pdf-search"
                                v-model="pdfSearch[delivery.id]"
                                :placeholder="$t('dash.settings.notifications.reports.history.search')"
                            >
                            <div class="pdf-grid">
                                <div class="pdf-chip" v-for="pdf in filteredPdfs(delivery)" :key="pdf.id" :class="{ disabled: downloading[pdf.id] }" @click="downloadPdf(delivery, pdf)">
                                    <Avatar :urlPicture="pageAvatar(pdf)" :typeNetwork="pdf.network || 'facebook'"></Avatar>
                                    <div class="pdf-page">
                                        <span class="pdf-name">{{ pdf.page_name || pdf.filename }}</span>
                                        <span class="pdf-network">{{ networkLabel(pdf.network) }}</span>
                                    </div>
                                    <i :class="downloading[pdf.id] ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'"></i>
                                </div>
                                <p class="no-pdf" v-if="!filteredPdfs(delivery).length">{{ $t('dash.settings.notifications.reports.popup.pages.no_results') }}</p>
                            </div>
                        </div>
                    </div>

                    <div class="history-pagination" v-if="historyPages > 1">
                        <div class="btn-outline-primary" :class="{ disabled: historyPage <= 1 }" @click="loadHistory(historyPage - 1)">{{ $t('dash.settings.notifications.reports.history.previous') }}</div>
                        <span>{{ historyPage }} / {{ historyPages }}</span>
                        <div class="btn-outline-primary" :class="{ disabled: historyPage >= historyPages }" @click="loadHistory(historyPage + 1)">{{ $t('dash.settings.notifications.reports.history.next') }}</div>
                    </div>
                </div>
            </template>
        </div>

        <ReportSchedulePopup
            v-if="popup.display"
            :key="popup.key"
            :display="popup.display"
            :data="popup.data"
            :pages="reportablePages"
            :groups="groups"
            :email="infoUser && infoUser.email"
            :saving="saving"
            @submit="onSubmit"
            @closePopUp="closePopup"
        ></ReportSchedulePopup>
    </div>
</template>

<script>
import moment from 'moment'
import ComponentSwitch from './../utils/switch.vue'
import Avatar from './../utils/avatar.vue'
import Tooltip from './../tooltip/tooltipInit.vue'
import ReportSchedulePopup from './report-schedule-popup.vue'
import BetaBadge from '../utils/BetaBadge.vue'
import ReportSchedules from '../../mixins/ReportSchedules'

// Réseaux couverts par les bilans (cf. engine newsletter.js SUPPORTED_NETWORKS)
const REPORT_NETWORKS = ['facebook', 'instagram', 'linkedin', 'tiktok']
const MONDAY = new Date(2026, 9, 12)
const NETWORK_LABELS = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', tiktok: 'TikTok' }

export default {
    name: 'report-schedules',

    components: {
        ComponentSwitch,
        Avatar,
        Tooltip,
        ReportSchedulePopup,
        BetaBadge,
    },

    mixins: [ReportSchedules],

    props: {
        infoUser: Object,
        // pages accessibles au user
        pages: {
            type: Array,
            default: () => []
        },
        groups: {
            type: Array,
            default: () => []
        },
    },

    data() {
        return {
            maxSchedules: 20,
            tab: 'schedules',
            loading: true,
            schedules: [],
            saving: false,
            sending: {},
            popup: { display: false, data: null, key: 0 },

            historyLoading: false,
            historyLoaded: false,
            deliveries: [],
            historyPage: 1,
            historyPages: 1,
            downloading: {},
            maxInlinePdfs: 3,
            expandedDeliveries: {},
            pdfSearch: {},
        };
    },

    computed: {
        reportablePages() {
            return this.pages.filter(page => REPORT_NETWORKS.includes(page.network))
        },

        pagesById() {
            return this.pages.reduce((acc, page) => ({ ...acc, [page.id]: page }), {})
        },
    },

    mounted() {
        this.refresh()
    },

    methods: {
        async refresh() {
            const res = await this.listReportSchedules()
            const body = res.body || {}
            if (body.success === true) this.schedules = body.report_schedules || []
            this.loading = false
        },

        notifyError(errorKey) {
            const specific = errorKey && this.$te(`dash.settings.notifications.reports.errors.${errorKey.replace('dash.error.', '')}`)
            this.$vs.notify({
                title: this.$t('dash.notifications.error_title'),
                text: specific
                    ? this.$t(`dash.settings.notifications.reports.errors.${errorKey.replace('dash.error.', '')}`)
                    : this.$t('dash.notifications.error_body'),
                color: 'error'
            })
        },

        schedulePages(schedule) {
            return (schedule.page_ids || []).map(id => this.pagesById[id]).filter(Boolean)
        },

        frequencyLabel(schedule) {
            const base = 'dash.settings.notifications.reports.frequency.'
            if (schedule.frequency === 'weekly') {
                const day = new Date(MONDAY)
                day.setDate(MONDAY.getDate() + (schedule.weekday || 1) - 1)
                const dayName = new Intl.DateTimeFormat(this.$i18n.locale || 'fr', { weekday: 'long' }).format(day)
                return this.$t(base + 'weekly', { day: dayName })
            }
            if (schedule.frequency === 'interval') return this.$t(base + 'interval', { n: schedule.interval_days })
            return this.$t(base + schedule.frequency)
        },

        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : ''
        },

        formatDateTime(date) {
            return date ? moment(date).format('DD/MM/YYYY HH:mm') : ''
        },

        openCreatePopup() {
            if (this.schedules.length >= this.maxSchedules) return this.notifyError('dash.error.report_schedules_limit_reached')
            this.popup = { display: true, data: null, key: this.popup.key + 1 }
        },

        openEditPopup(schedule) {
            this.popup = { display: true, data: schedule, key: this.popup.key + 1 }
        },

        closePopup() {
            this.popup = { ...this.popup, display: false, data: null }
        },

        async onSubmit(payload) {
            this.saving = true
            const { id, ...body } = payload
            const res = id ? await this.updateReportSchedule(id, body) : await this.createReportSchedule(body)
            this.saving = false

            const resBody = res.body || {}
            if (resBody.success !== true) return this.notifyError(resBody.error)

            this.closePopup()
            this.refresh()
        },

        async toggleEnabled(checked, id) {
            const schedule = this.schedules.find(s => s.id === id)
            if (!schedule) return
            schedule.enabled = checked
            const res = await this.updateReportSchedule(id, { enabled: checked })
            const body = res.body || {}
            if (body.success !== true) {
                schedule.enabled = !checked
                return this.notifyError(body.error)
            }
            Object.assign(schedule, body.report_schedule)
        },

        async sendNow(schedule) {
            if (this.sending[schedule.id]) return
            this.$set(this.sending, schedule.id, true)
            const res = await this.sendReportScheduleNow(schedule.id)
            this.$set(this.sending, schedule.id, false)

            const body = res.body || {}
            if (body.success !== true) return this.notifyError(body.error)

            this.$vs.notify({
                title: this.$t('dash.settings.notifications.reports.send_now_title'),
                text: this.$t('dash.settings.notifications.reports.send_now_success', { email: this.infoUser && this.infoUser.email }),
                color: 'success'
            })
        },

        async remove(schedule) {
            if (!confirm(this.$t('dash.settings.notifications.reports.delete_confirm'))) return
            const res = await this.deleteReportSchedule(schedule.id)
            const body = res.body || {}
            if (body.success !== true) return this.notifyError(body.error)
            this.schedules = this.schedules.filter(s => s.id !== schedule.id)
        },

        openHistory() {
            this.tab = 'history'
            if (!this.historyLoaded) this.loadHistory(1)
        },

        async loadHistory(page) {
            if (page < 1 || (this.historyLoaded && page > this.historyPages)) return
            this.historyLoading = true
            const res = await this.listReportDeliveries(page)
            const body = res.body || {}
            this.historyLoading = false
            if (body.success !== true) return this.notifyError(body.error)

            this.deliveries = body.report_deliveries || []
            this.expandedDeliveries = {}
            this.pdfSearch = {}
            this.historyPage = body.page || page
            this.historyPages = body.pages || 1
            this.historyLoaded = true
        },

        // Avatar courant de la page (les URL d'avatar expirent, on ne les stocke pas avec le PDF)
        pageAvatar(pdf) {
            const page = this.pagesById[pdf.page_id]
            return (page && page.avatar) || undefined
        },

        networkLabel(network) {
            return NETWORK_LABELS[network] || network || ''
        },

        toggleDelivery(id) {
            this.$set(this.expandedDeliveries, id, !this.expandedDeliveries[id])
        },

        filteredPdfs(delivery) {
            const q = (this.pdfSearch[delivery.id] || '').trim().toLowerCase()
            if (!q) return delivery.pdfs
            return delivery.pdfs.filter(pdf => (pdf.page_name || pdf.filename || '').toLowerCase().includes(q) ||
                (pdf.network || '').includes(q))
        },

        async downloadPdf(delivery, pdf) {
            if (this.downloading[pdf.id]) return
            this.$set(this.downloading, pdf.id, true)
            const res = await this.getReportDeliveryPdfUrl(delivery.id, pdf.id)
            this.$set(this.downloading, pdf.id, false)

            const body = res.body || {}
            if (body.success !== true || !body.url) return this.notifyError(body.error)
            window.open(body.url, '_blank')
        },
    },
};
</script>

<style lang="scss" scoped>
.card-reports {
    width: 100%;
    max-width: 100% !important;


    .content {
        padding: 20px 24px !important;
    }

    .reports-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;

        p {
            font-size: 13.6px;
            color: #9FA4A8;
            font-weight: 500;
            margin: 0;
            max-width: 640px;
        }

        .btn-primary {
            flex-shrink: 0;
        }
    }

    .disabled {
        opacity: 0.45;
        pointer-events: none;
    }

    .reports-tabs {
        display: flex;
        gap: 20px;
        margin: 20px 0 8px;
        border-bottom: 1.6px solid #F1EDF5;

        span {
            padding: 8px 0;
            font-size: 13px;
            font-weight: 600;
            color: #9ba3b7;
            cursor: pointer;
            border-bottom: 2px solid transparent;
            margin-bottom: -1.6px;

            &.active {
                color: #6b1f4d;
                border-bottom-color: #6b1f4d;
            }
        }
    }

    .reports-loading {
        padding: 24px;
        text-align: center;
        color: #9ba3b7;
    }

    .reports-empty {
        padding: 28px 16px;
        text-align: center;
        color: #9ba3b7;
        font-size: 13px;

        i {
            font-size: 26px;
            color: #d7c9e0;
            margin-bottom: 8px;
        }

        .empty-title {
            font-weight: 600;
            color: #555e75;
            margin-bottom: 4px;
        }
    }

    .report-row, .delivery-row {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px 0;
        border-bottom: 1px solid #f1f2f7;
        flex-wrap: wrap;

        &:last-child {
            border-bottom: none;
        }

        &.paused .report-main, &.paused .report-pages {
            opacity: 0.5;
        }
    }

    .report-main {
        flex: 1 1 240px;
        min-width: 0;

        .report-name {
            font-size: 14px;
            font-weight: 600;
            color: #121826;
            margin: 0 0 4px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .report-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 4px 14px;
            margin: 0;
            font-size: 12px;
            color: #9ba3b7;

            i {
                margin-right: 3px;
            }

            .paused-label {
                color: #b64985;
                font-weight: 600;
            }
        }
    }

    .report-pages {
        display: flex;
        align-items: center;
        gap: 4px;

        .all-pages {
            font-size: 12px;
            font-weight: 600;
            color: #6b1f4d;
            background: #f9f0f5;
            border-radius: 10px;
            padding: 3px 10px;
        }

        .more {
            font-size: 12px;
            color: #9ba3b7;
            margin-left: 2px;
        }

        ::v-deep .avatar {
            width: 26px;
            height: 26px;
        }
    }

    .report-actions {
        display: flex;
        align-items: center;
        gap: 6px;

        .switch {
            margin-right: 6px;
        }

        .icon-btn {
            width: 30px;
            height: 30px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #555e75;
            cursor: pointer;
            transition: background 0.15s, color 0.15s;

            &:hover {
                background: #f6f1fa;
                color: #6b1f4d;
            }

            &.danger:hover {
                color: #e14e4e;
            }
        }
    }

    .delivery-pdfs {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        flex: 1 1 260px;
        justify-content: flex-end;

        .no-pdf {
            font-size: 12px;
            color: #9ba3b7;
        }
    }

    .pdf-chip {
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 240px;
        min-width: 0;
        padding: 4px 10px 4px 4px;
        border: 1px solid #e0d0ea;
        border-radius: 18px;
        cursor: pointer;
        background: #fff;
        transition: background 0.15s, border-color 0.15s;

        ::v-deep .avatar {
            width: 28px;
            height: 28px;
            flex-shrink: 0;
        }

        .pdf-page {
            display: flex;
            flex-direction: column;
            min-width: 0;
            line-height: 1.25;
        }

        .pdf-name {
            font-size: 12px;
            font-weight: 600;
            color: #121826;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .pdf-network {
            font-size: 11px;
            color: #9ba3b7;
        }

        > i {
            margin-left: auto;
            padding-left: 4px;
            font-size: 12px;
            color: #b64985;
        }

        &:hover {
            background: #f6f1fa;
            border-color: #b64985;
        }
    }

    .pdf-more {
        font-size: 12px;
        font-weight: 600;
        color: #6b1f4d;
        padding: 6px 10px;
        border-radius: 18px;
        background: #f9f0f5;
        cursor: pointer;
        white-space: nowrap;

        &:hover {
            background: #f1e2ec;
        }
    }

    .pdf-panel {
        flex-basis: 100%;
        padding: 10px;
        border-radius: 6px;
        background: #faf8fc;

        .pdf-search {
            width: 100%;
            max-width: 280px;
            box-sizing: border-box;
            margin-bottom: 10px;
            padding: 6px 10px;
            border: 1px solid #e3e6ee;
            border-radius: 6px;
            font-size: 12px;
            outline: none;
        }

        .pdf-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
            gap: 8px;
            max-height: 320px;
            overflow-y: auto;

            .pdf-chip {
                max-width: none;
            }
        }

        .no-pdf {
            font-size: 12px;
            color: #9ba3b7;
            margin: 0;
        }
    }

    .history-pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding-top: 12px;
        font-size: 12px;
        color: #555e75;
    }
}
</style>
